import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Drawer, Grid, Badge, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ShoppingCartDialog from '../ShoppingCartDialog';
import UserMenu from 'components/UserMenu';
import { OrderInfo, Shopper, OrderProduct, ProductApi } from 'common/types';
import ConfirmBtns from './ConfirmBtns';
import { getPricingString } from 'common/utils/common';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import * as ShopSelectors from 'selectors/ShopSelectors';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';

interface Props {
	rentalInfo: OrderInfo;
	rentalProducts: OrderProduct[];
	activeShopperId: string;
	setActiveShopperId: (id: string) => void;
	shoppers: Shopper[];
	addNewShopper: (id: string) => void;
	validateRentalAndGoToRoute: (route: string) => void;
	stockProducts: ProductApi[];
	overbookedProducts?: OrderProduct[];
}
const BottomBar = (props: Props) => {
	const {
		rentalInfo,
		rentalProducts,
		activeShopperId,
		setActiveShopperId,
		shoppers,
		addNewShopper,
		validateRentalAndGoToRoute,
		stockProducts,
		overbookedProducts,
	} = props;
	const classes = useStyles();
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const currency = getCurrencyObjectFromCurrencyCode(rentalInfo.pricing.currency, shopCurrency);
	const [shoppingCartOpen, setShoppingCartOpen] = useState<boolean>(false);
	const productsCount = rentalProducts.length;

	const addShopper = () => {
		const shopperId = newFirestoreId();
		addNewShopper(shopperId);
		setActiveShopperId(shopperId);
	};

	const renderUserMenu = () => {
		const activeShopperIndex = props.shoppers.reduce(
			(activeIndex: number, shopper, index) =>
				shopper.id === activeShopperId ? index : activeIndex,
			0,
		);
		return (
			<UserMenu
				shoppers={shoppers}
				activeShopperIndex={activeShopperIndex}
				activeShopperId={activeShopperId}
				changeActiveShopperId={setActiveShopperId}
				addNewShopper={() => addShopper()}
			/>
		);
	};
	return (
		<Drawer
			open={productsCount > 0}
			variant="persistent"
			classes={{
				paper: classes.bottomMenu,
			}}
			anchor={'bottom'}
		>
			<Grid
				container
				justifyContent="flex-start"
				alignItems="center"
				className={classes.iconWrapper}
				onClick={() => setShoppingCartOpen(!shoppingCartOpen)}
			>
				<Badge className={classes.leftIcon} badgeContent={productsCount} color="primary">
					<ShoppingCartIcon color="inherit" />
				</Badge>
				<Typography variant="subtitle1" color="inherit">
					{getPricingString(rentalInfo.pricing.total || 0, currency)}
				</Typography>
			</Grid>
			{renderUserMenu()}
			<Grid container justifyContent="flex-end" alignItems="center" className={classes.iconWrapper}>
				<ConfirmBtns
					rentalProducts={rentalProducts}
					shoppers={shoppers}
					validateRentalAndGoToRoute={(route: string) => validateRentalAndGoToRoute(route)}
					hasOverbooking={!!overbookedProducts?.length}
				/>
			</Grid>
			<ShoppingCartDialog
				open={shoppingCartOpen}
				onClose={() => setShoppingCartOpen(false)}
				activeShopperId={activeShopperId}
				changeActiveShopperId={setActiveShopperId}
				stockProducts={stockProducts}
			/>
		</Drawer>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		bottomMenu: {
			height: 64,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			flexDirection: 'row',
			padding: '8px 16px',
			overflow: 'unset',
			backgroundColor: theme.palette.background.paper,
		},
		iconWrapper: {
			display: 'flex',
			alignItems: 'center',
			height: '100%',
			cursor: 'pointer',
			flex: '1',
		},
		leftIcon: {
			marginRight: 12,
		},
	}),
);

export default BottomBar;
